import React, { useState, useEffect } from 'react';
import { Star, Calendar, AlertCircle, TrendingUp, Eye } from 'lucide-react';
import { Fair } from '../types';
import { API_BASE_URL } from '../services/api';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling

interface FeaturedHistory {
  id: number;
  fair_id: number;
  fair_name: string;
  city: string;
  featured_at: string;
  unfeatured_at: string | null;
}

export default function FeaturedFairDashboard() {
  const [currentFeatured, setCurrentFeatured] = useState<Fair[]>([]);
  const [neverFeatured, setNeverFeatured] = useState<Fair[]>([]);
  const [candidates, setCandidates] = useState<Fair[]>([]);
  const [history, setHistory] = useState<FeaturedHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'current' | 'candidates' | 'never' | 'history'>('current');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load all data in parallel - handle each independently
      const results = await Promise.allSettled([
        fetch(`${API_BASE_URL}/featured-fairs`, { headers }).then(r => r.ok ? r.json() : Promise.reject(r)),
        fetch(`${API_BASE_URL}/admin/featured-fairs/never-featured`, { headers }).then(r => r.ok ? r.json() : Promise.reject(r)),
        fetch(`${API_BASE_URL}/admin/featured-fairs/candidates?days=30`, { headers }).then(r => r.ok ? r.json() : Promise.reject(r)),
        fetch(`${API_BASE_URL}/admin/featured-fairs/history?limit=50`, { headers }).then(r => r.ok ? r.json() : Promise.reject(r))
      ]);

      // Set data for successful responses, empty array for failures
      setCurrentFeatured(results[0].status === 'fulfilled' ? results[0].value : []);
      setNeverFeatured(results[1].status === 'fulfilled' ? results[1].value : []);
      setCandidates(results[2].status === 'fulfilled' ? results[2].value : []);
      setHistory(results[3].status === 'fulfilled' ? results[3].value : []);

      // Log any failures
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['featured-fairs', 'never-featured', 'candidates', 'history'];
          console.error(`Failed to load ${endpoints[index]}:`, result.reason);
        }
      });

      // Only set error if ALL endpoints failed
      const allFailed = results.every(r => r.status === 'rejected');
      if (allFailed) {
        setError('Failed to load dashboard data. Please check your authentication.');
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load dashboard data:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDuration = (start: string, end: string | null) => {
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const days = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    return `${days} day${days !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-red-800">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Featured Artist Fairs Dashboard</h1>
        <p className="text-gray-600 mt-1">Manage and monitor featured artist fairs across all cities</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-moonbone border border-teal-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary font-medium">Currently Featured</p>
              <p className="text-3xl font-bold text-ink mt-1">{currentFeatured.length}</p>
            </div>
            <Star className="h-8 w-8 text-secondary" />
          </div>
        </div>

        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary font-medium">Ready to Feature</p>
              <p className="text-3xl font-bold text-teal-900 mt-1">{candidates.length}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-secondary" />
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">Never Featured</p>
              <p className="text-3xl font-bold text-yellow-900 mt-1">{neverFeatured.length}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Total History</p>
              <p className="text-3xl font-bold text-purple-900 mt-1">{history.length}</p>
            </div>
            <Calendar className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'current', label: 'Currently Featured', count: currentFeatured.length },
            { id: 'candidates', label: 'Candidates', count: candidates.length },
            { id: 'never', label: 'Never Featured', count: neverFeatured.length },
            { id: 'history', label: 'History', count: history.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as 'current' | 'candidates' | 'never' | 'history')}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                ${selectedTab === tab.id
                  ? 'border-dusty-teal text-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {selectedTab === 'current' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Currently Featured Artist Fairs</h2>
            {currentFeatured.length === 0 ? (
              <p className="text-gray-500">No artist fairs are currently featured</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentFeatured.map(fair => (
                  <div key={fair.id} className="bg-white border border-teal-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{fair.name}</h3>
                      <Star className="h-5 w-5 text-secondary fill-teal-600" />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{fair.address.city}, {fair.address.state}</p>
                    <a
                      href={`/admin/fairs/${fair.id}/edit`}
                      className="inline-flex items-center text-sm text-secondary hover:text-ink transition-colors"
                      aria-label="View artist fair"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedTab === 'candidates' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Eligible Candidates (Not Featured in 30+ Days)</h2>
            {candidates.length === 0 ? (
              <p className="text-gray-500">No eligible candidates at this time</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {candidates.map(fair => (
                  <div key={fair.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                    <h3 className="font-semibold text-gray-900 mb-2">{fair.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{fair.address.city}, {fair.address.state}</p>
                    <a
                      href={`/admin/fairs/${fair.id}/edit`}
                      className="inline-flex items-center text-sm text-secondary hover:text-ink transition-colors"
                      aria-label="View artist fair"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedTab === 'never' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Never Featured Artist Fairs</h2>
            {neverFeatured.length === 0 ? (
              <p className="text-gray-500">All artist fairs have been featured at least once</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {neverFeatured.map(fair => (
                  <div key={fair.id} className="bg-white border border-yellow-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                    <h3 className="font-semibold text-gray-900 mb-2">{fair.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{fair.address.city}, {fair.address.state}</p>
                    <a
                      href={`/admin/fairs/${fair.id}/edit`}
                      className="inline-flex items-center text-sm text-secondary hover:text-ink transition-colors"
                      aria-label="View artist fair"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedTab === 'history' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Feature History</h2>
            {history.length === 0 ? (
              <p className="text-gray-500">No feature history available</p>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Artist Fair
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        City
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Featured On
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unfeatured On
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {history.map(entry => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {entry.fair_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.city}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(entry.featured_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.unfeatured_at ? formatDate(entry.unfeatured_at) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-moonbone text-ink">
                              Currently Featured
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getDuration(entry.featured_at, entry.unfeatured_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
